/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { Drawer, CircularProgress } from "@mui/material";
import DeviceDrawer from "./DeviceDrawer";
import { useStoreDispatch } from "../../store/hooks";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { IEvent } from "../../store/api/devices/types";
import { DATEFORMATS, DIALOGOPENFOR } from "../../globals/globals";
import {
  setOpenDeviceDrawer,
  setDrawerCounts,
  setSelectedDevice,
  setSelectedDeviceAlarms,
  setSelectedDeviceAlarmCount,
} from "../../store/api/devices/reducers";
import { setIsLoadingStatus, setOpenToast, setConfirmDialog } from "../../store/helpers/helperReducers";
import { convertEpochToGMT, errorMessage } from "../../utils/helpers";
import { useGetDeviceFullDetailMutation } from "../../store/api/deviceConfig/deviceConfigApiSlice";
import {
  useGetDrawerCountsMutation,
  useGetAlarmsByDeviceKeyMutation,
  useGetAlarmTicketMutation,
  useCreateOrUpdateAlarmTicketMutation,
  useDeleteAlarmTicketMutation,
} from "../../store/api/devices/deviceApiSlice";
import { isOpenDeviceDrawerSelector, selectedDeviceSelector } from "../../store/api/devices/selectors";
import ConfirmDialog from "../Shared/Dialogs/ConfirmDialog";
import { confirmDialogSelector } from "../../store/helpers/helperSelectors";
import { IFullDeviceDetails } from "../../store/api/deviceConfig/types";
import { Card, CardContent, CardTitle } from "../ui/card";
import { Icon } from "../Shared/Icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

const SEVERITY_ICON: Record<string, { name: string; className: string }> = {
  Critical: { name: "error", className: "text-destructive" },
  Major: { name: "error_outline", className: "text-warning" },
  Minor: { name: "warning", className: "text-yellow-500" },
  Warning: { name: "warning_amber", className: "text-yellow-400" },
  Info: { name: "info", className: "text-muted-foreground" },
};

function NameValueField({ label, value }: { label: string; value: React.ReactNode }) {
  const text = typeof value === "string" ? value : undefined;
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground">{label}</span>
      {text ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="font-medium truncate">{text || "—"}</span>
          </TooltipTrigger>
          <TooltipContent className="z-[1400]">
            <p>{text}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <span className="font-medium truncate">{value || "—"}</span>
      )}
    </div>
  );
}

function EditableField({
  label,
  value,
  isEditing,
  editValue,
  onEditChange,
  placeholder = "—",
}: {
  label: string;
  value: string;
  isEditing: boolean;
  editValue: string;
  onEditChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground">{label}</span>
      {isEditing ? (
        <input
          type="text"
          value={editValue}
          onChange={(e) => onEditChange(e.target.value)}
          className="h-8 rounded-md border border-[var(--border)] bg-transparent px-2 text-sm text-foreground outline-none focus:border-[var(--ring)] focus:ring-1 focus:ring-[var(--ring)]"
        />
      ) : (
        <span className="font-medium truncate" title={value || placeholder}>
          {value || placeholder}
        </span>
      )}
    </div>
  );
}

interface IEventDrawerProps {
  data?: IEvent;
  closedButtonClicked: (arg: boolean) => void;
  setRowClicked: (arg: boolean) => void;
  isAlarmContext?: boolean;
}

const EventDrawer: React.FC<IEventDrawerProps> = ({ data, closedButtonClicked, isAlarmContext = false }) => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const [isOpen, setOpen] = React.useState(false);
  React.useEffect(() => { setOpen(true); }, []);
  const [drawerWidth, setDrawerWidth] = React.useState("560px");
  const isOpenDeviceDrawer = useSelector(isOpenDeviceDrawerSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);

  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();
  const [getAlarmTicket] = useGetAlarmTicketMutation();
  const [createOrUpdateAlarmTicket, { isLoading: isSavingAlarmTicket }] = useCreateOrUpdateAlarmTicketMutation();
  const [deleteAlarmTicket] = useDeleteAlarmTicketMutation();
  const [ticketId, setTicketId] = React.useState("");
  const [assignee, setAssignee] = React.useState("");
  const alarmTrackId = (data as any)?.alarmTrackId as string | undefined;
  const alarmObjectKey = (data as any)?.objectKey as string | undefined;
  const isAlarm = Boolean(data?.isAlarm);
  const [isEditingAlarm, setIsEditingAlarm] = React.useState(false);
  const [editTicketId, setEditTicketId] = React.useState("");
  const [editAssignee, setEditAssignee] = React.useState("");
  const openConfirmDialog = useSelector(confirmDialogSelector);

  const severityIcon = SEVERITY_ICON[data?.severity || ""] || SEVERITY_ICON.Info;

  const validateAlphanumeric = (value: string, maxLength: number, fieldName: string): string => {
    if (value.length > maxLength) {
      return `${fieldName} cannot exceed ${maxLength} characters`;
    }
    if (!/^[^<>?;%/\\&\[\]{}]*$/.test(value)) {
      return `${fieldName} contains invalid characters`;
    }
    return "";
  };

  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event.type === "keydown" && ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")) {
      return;
    }

    dispatch(setOpenDeviceDrawer(false));
    setOpen(open);
    closedButtonClicked(open);
    setDrawerWidth("560px");
  };

  const openDeviceDrawerHandler = async (objectKey?: string) => {
    dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: true, isLoadingAlarms: true }));
    dispatch(setOpenDeviceDrawer(true));
    try {
      const args = { filters: `key=objectKey&value=${objectKey}&weightType=FULL`, objectKey };

      const [drawerCountsResponse, deviceDetailResponse, alarmResponse] = await Promise.all([
        getDrawerCounts({ deviceKey: objectKey, filters: "" }),
        getDeviceFullDetail(args),
        getAlarmsByDeviceKey({ deviceKey: objectKey, filters: "?sort=retention%2Cdesc&page=0&size=50" }),
      ]);

      if ("error" in drawerCountsResponse || "error" in deviceDetailResponse || "error" in alarmResponse) {
        const { error = "" } = { ...drawerCountsResponse, ...deviceDetailResponse, ...alarmResponse } as any;
        throw !!error && error;
      }

      const { data: configCount } = drawerCountsResponse;
      const { data: deviceData } = deviceDetailResponse;
      const { data: alarmData } = alarmResponse;

      if (!!configCount && !!deviceData && deviceData.data[0] && alarmData) {
        dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
        dispatch(setDrawerCounts(configCount));
        dispatch(setSelectedDevice({ ...deviceData.data[0], ...deviceData?.deviceParams }));
        dispatch(setSelectedDeviceAlarms(alarmData.data));
        dispatch(setSelectedDeviceAlarmCount(alarmData.totalItems || 0));
      }
      setDrawerWidth("560px");
    } catch (error: any) {
      dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
      dispatch(setOpenDeviceDrawer(false));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  function setCallback() {
    setDrawerWidth("560px");
  }

  React.useEffect(() => {
    let isMounted = true;

    const fetchAlarmTicket = async () => {
      if (!alarmTrackId) return;
      try {
        const response = await getAlarmTicket({ alarmId: alarmTrackId }).unwrap();
        if (!isMounted) return;
        setTicketId(response?.ticketId || "");
        setAssignee(response?.assignedTo || "");
      } catch (error) {
        if (!isMounted) return;
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
      }
    };

    fetchAlarmTicket();

    return () => {
      isMounted = false;
    };
  }, [alarmTrackId, dispatch, getAlarmTicket]);

  const handleSaveAlarmTicket = async () => {
    if (!alarmObjectKey) return;

    const trimmedTicket = editTicketId?.trim();
    const trimmedOwner = editAssignee?.trim();

    if (!trimmedTicket || !trimmedOwner) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Ticket ID and Owner are required." }));
      return;
    }

    const ticketError = validateAlphanumeric(trimmedTicket, 15, "Ticket ID");
    const ownerError = validateAlphanumeric(trimmedOwner, 25, "Owner");

    if (ticketError || ownerError) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: ticketError || ownerError }));
      return;
    }

    try {
      await createOrUpdateAlarmTicket({ alarmId: alarmObjectKey, ticketId: trimmedTicket, assignee: trimmedOwner }).unwrap();
      setTicketId(trimmedTicket);
      setAssignee(trimmedOwner);
      setIsEditingAlarm(false);
      dispatch(setOpenToast({ openToast: true, toastType: "success", message: "Alarm ticket updated." }));
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleDeleteAlarmTicket = async () => {
    if (!alarmObjectKey) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Invalid alarm id." }));
      return;
    }

    try {
      await deleteAlarmTicket({ alarmId: alarmObjectKey }).unwrap();
      setTicketId("");
      setAssignee("");
      setEditTicketId("");
      setEditAssignee("");
      setIsEditingAlarm(false);
      dispatch(setOpenToast({ openToast: true, toastType: "success", message: "Alarm ticket deleted." }));
    } catch (error) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleDeleteAlarmConfirm = (action: string) => {
    if (action === "delete") {
      handleDeleteAlarmTicket();
    }
  };

  const canShowDeviceLink =
    !!data?.hostName &&
    data.hostName.toLocaleLowerCase() !== "server" &&
    !location.pathname.includes("device");

  return (
    <>
      {isOpenDeviceDrawer && !!selectedDevice?.nodeType && <DeviceDrawer data={selectedDevice} callbackFun={setCallback} />}
      {openConfirmDialog === DIALOGOPENFOR.ALARMTRACKID && (
        <ConfirmDialog actionName="Delete" handleClickAction={handleDeleteAlarmConfirm}>
          Are you sure you want to delete the alarm tracking ID?
        </ConfirmDialog>
      )}
      <Drawer
        PaperProps={{
          sx: {
            width: drawerWidth,
            maxWidth: "90vw",
            height: "100%",
            top: 0,
            backgroundColor: "var(--color-background)",
            borderLeft: "1px solid var(--color-border)",
            borderRadius: "10px 0 0 10px",
            boxShadow: "-4px 0 24px 0 rgba(0,0,0,0.08)",
            overflow: "hidden",
          },
        }}
        ModalProps={{ disableEnforceFocus: true }}
        anchor="right"
        open={isOpen}
        onClose={toggleDrawer(false)}
      >
        <div className="flex flex-col h-full min-h-0 bg-background">
          {/* ─── Header ─── */}
          <div className="relative pr-12 shrink-0 px-4 pt-0 pb-2">
            {/* Close button */}
            <button
              type="button"
              className="absolute right-2 top-2 h-8 w-8 shrink-0 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-transparent text-foreground border-0 p-0 focus-visible:outline-none"
              aria-label="Close"
              onClick={toggleDrawer(false) as any}
            >
              <Icon name="close" size={20} />
            </button>

            {/* Severity + Type heading */}
            <div className="flex items-center gap-2 mt-1">
              <Icon name={severityIcon.name} size={22} className={severityIcon.className} />
              <Tooltip>
                <TooltipTrigger asChild>
                  <h2 className="text-xl font-semibold truncate pr-8 text-foreground">
                    {data?.severity || "Unknown"} – {data?.displayName || data?.eventType || "Unknown"}
                  </h2>
                </TooltipTrigger>
                <TooltipContent className="z-[1400]">
                  <p>{data?.severity || "Unknown"} – {data?.displayName || data?.eventType || "Unknown"}</p>
                </TooltipContent>
              </Tooltip>
            </div>

            {/* Device name link */}
            {canShowDeviceLink && (
              <div className="flex items-center gap-1 mt-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-sm text-[var(--link)] hover:text-[var(--link-hover)] bg-transparent border-0 p-0 cursor-pointer inline-flex items-center gap-1 transition-colors max-w-[calc(100%-24px)] truncate"
                      onClick={() => openDeviceDrawerHandler(data?.parentKey)}
                    >
                      <span className="truncate">{data?.hostName}</span>
                      <Icon name="open_in_new" size={14} className="text-muted-foreground shrink-0" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="z-[1400]">
                    <p>{data?.hostName}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>

          {/* ─── Scrollable content ─── */}
          <div className="flex-1 min-h-0 overflow-auto px-4 pt-6 pb-4 space-y-4">
            {/* Card 1: Event/Alarm Details */}
            <Card>
              <CardContent className="pt-4 space-y-6">
                <CardTitle className="text-sm font-semibold">
                  {isAlarmContext ? "Alarm details" : "Event details"}
                </CardTitle>
                <div className="grid grid-cols-3 gap-x-3 gap-y-6 text-sm">
                  {/* Row 1 */}
                  {canShowDeviceLink ? (
                    <div className="flex flex-col gap-1">
                      <span className="text-muted-foreground">Device</span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="font-medium text-[var(--link)] hover:text-[var(--link-hover)] bg-transparent border-0 p-0 cursor-pointer text-left text-sm transition-colors truncate"
                            onClick={() => openDeviceDrawerHandler(data?.parentKey)}
                          >
                            {data?.hostName}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="z-[1400]">
                          <p>{data?.hostName}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  ) : (
                    <NameValueField label="Device" value={data?.hostName || "Unknown"} />
                  )}
                  <div className="flex flex-col gap-1">
                    <span className="text-muted-foreground">Severity</span>
                    <span className="font-medium inline-flex items-center gap-1.5">
                      <Icon name={severityIcon.name} size={16} className={`shrink-0 ${severityIcon.className}`} />
                      {data?.severity || "—"}
                    </span>
                  </div>
                  <NameValueField
                    label="Time Occurred"
                    value={convertEpochToGMT(data?.occuredDateAndTimeMs || 0, DATEFORMATS.TS)}
                  />

                  {/* Row 2 */}
                  <NameValueField
                    label="Time Updated"
                    value={convertEpochToGMT(data?.retention || 0, DATEFORMATS.TS)}
                  />
                  <NameValueField label="Managed Object" value={data?.managedObject || "—"} />
                  <NameValueField label="Event Type" value={data?.eventType || "—"} />

                  {/* Row 3: Description (full width) */}
                  <div className="col-span-3 flex flex-col gap-1">
                    <span className="text-muted-foreground">Description</span>
                    <span className="font-medium">{data?.description || "—"}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Ticket Tracking (alarm context only) */}
            {isAlarm && isAlarmContext && (
              <Card>
                <CardContent className="pt-4 space-y-6">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold">Ticket Tracking</CardTitle>
                    <div className="flex items-center gap-1">
                      {isEditingAlarm ? (
                        isSavingAlarmTicket ? (
                          <CircularProgress size={18} />
                        ) : (
                          <>
                            <button
                              type="button"
                              className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-muted border-0 p-0 transition-colors"
                              aria-label="Cancel"
                              onClick={() => {
                                setIsEditingAlarm(false);
                                setEditTicketId(ticketId);
                                setEditAssignee(assignee);
                              }}
                            >
                              <Icon name="close" size={16} className="text-destructive" />
                            </button>
                            <button
                              type="button"
                              className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-muted border-0 p-0 transition-colors"
                              aria-label="Save"
                              onClick={handleSaveAlarmTicket}
                            >
                              <Icon name="done" size={16} className="text-[var(--success)]" />
                            </button>
                          </>
                        )
                      ) : (
                        <>
                          <button
                            type="button"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-muted border-0 p-0 transition-colors"
                            aria-label="Edit"
                            onClick={() => {
                              setEditTicketId(ticketId);
                              setEditAssignee(assignee);
                              setIsEditingAlarm(true);
                            }}
                          >
                            <Icon name="edit" size={16} className="text-muted-foreground" />
                          </button>
                          <button
                            type="button"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-md bg-transparent hover:bg-muted border-0 p-0 transition-colors disabled:opacity-40"
                            aria-label="Delete"
                            disabled={!ticketId.trim() || !assignee.trim()}
                            onClick={() => dispatch(setConfirmDialog(DIALOGOPENFOR.ALARMTRACKID))}
                          >
                            <Icon name="delete" size={16} className="text-muted-foreground" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-x-3 gap-y-6 text-sm">
                    <EditableField
                      label="Ticket ID"
                      value={ticketId}
                      isEditing={isEditingAlarm}
                      editValue={editTicketId}
                      onEditChange={setEditTicketId}
                    />
                    <EditableField
                      label="Owner"
                      value={assignee}
                      isEditing={isEditingAlarm}
                      editValue={editAssignee}
                      onEditChange={setEditAssignee}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default EventDrawer;
